import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faCheckCircle, faInfoCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import DOMPurify from 'dompurify';

const variantConfig = {
  warning: {
    icon: faExclamationCircle,
    role: 'status',
    ariaLive: 'polite',
    containerClass: 'w-full mb-5 p-4 rounded border-l-4 flex gap-4 items-start bg-garfield-bg-warning text-garfield-fg-warning border-garfield-border-warning',
    textClass: 'text-garfield-fg-warning',
    linkClass: 'underline font-bold text-garfield-link-warning focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-garfield-link-warning',
  },
  success: {
    icon: faCheckCircle,
    role: 'status',
    ariaLive: 'polite',
    containerClass: 'w-full mb-5 p-4 rounded border-l-4 flex gap-4 items-start bg-garfield-bg-success text-garfield-fg-success border-garfield-border-success',
    textClass: 'text-garfield-fg-success',
    linkClass: 'underline font-bold text-garfield-link-success focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-garfield-link-success',
  },
  info: {
    icon: faInfoCircle,
    role: 'status',
    ariaLive: 'polite',
    containerClass: 'w-full mb-5 p-4 rounded border-l-4 flex gap-4 items-start bg-garfield-bg-info text-garfield-fg-info border-garfield-border-info',
    textClass: 'text-garfield-fg-info',
    linkClass: 'underline font-bold text-garfield-link-info focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-garfield-link-info',
  },
  danger: {
    icon: faTimesCircle,
    role: 'alert',
    ariaLive: 'assertive',
    containerClass: 'w-full mb-5 p-4 rounded border-l-4 flex gap-4 items-start bg-garfield-bg-danger text-garfield-fg-danger border-garfield-border-danger',
    textClass: 'text-garfield-fg-danger',
    linkClass: 'underline font-bold text-garfield-link-danger focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-garfield-link-danger',
  },
};

function Alert({ variant, title, body, children }) {
  const config = variantConfig[variant];

  if (!config) {
    console.error(`Invalid alert variant: ${variant}`);
    return null;
  }

  // Sanitize HTML content from backend
  const sanitizeHTML = (html) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['a', 'strong', 'em', 'br', 'p'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ADD_ATTR: ['rel'], // Ensure rel can be set
    });
  };

  // Process HTML to add proper link styling and security attributes
  const processContent = (htmlContent) => {
    const sanitized = sanitizeHTML(htmlContent);

    // Parse sanitized HTML to enhance links
    const wrapper = document.createElement('div');
    wrapper.innerHTML = sanitized;

    // Add classes and security attributes to all links
    const links = wrapper.querySelectorAll('a');
    links.forEach(link => {
      // Add variant-specific link styling using static class names
      link.className = config.linkClass;

      // Add security attributes for external links
      const href = link.getAttribute('href');
      if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
        link.setAttribute('rel', 'noopener noreferrer');
        link.setAttribute('target', '_blank');
      }
    });

    return wrapper.innerHTML;
  };

  const content = body || children;
  const processedContent = typeof content === 'string' ? processContent(content) : content;

  return (
    <div
      role={config.role}
      aria-live={config.ariaLive}
      className={config.containerClass}
    >
      <FontAwesomeIcon
        icon={config.icon}
        className="self-start mt-1 flex-shrink-0"
        size="lg"
        aria-hidden="true"
      />
      <div className="flex-1">
        {title && (
          <h3 className={`font-bold mb-2 ${config.textClass}`}>
            {title}
          </h3>
        )}
        <div
          className={config.textClass}
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />
      </div>
    </div>
  );
}

export default Alert;
